/**
 * Configuración → Turnos (`/configuracion/turnos`).
 *
 * Puerto de `Shift_cat` + `views/shift_cat/index.php`: el catálogo de turnos
 * (título + hora de inicio y fin). El sistema **no guarda el turno en la venta**:
 * clasifica las transacciones por su hora comparándolas con estas franjas (así lo
 * hacen `Kardex_model` y `Liquidaciones_model`).
 *
 * Reglas conservadas: los horarios **no pueden solaparse** (con el mismo mensaje
 * que el panel anterior, indicando con qué turno choca) y un turno puede cruzar
 * la medianoche.
 *
 * Dos diferencias anotadas:
 * - Se corrige el algoritmo de solape: el anterior sólo desplazaba el fin del
 *   turno nuevo, así que no veía que un turno de mañana caiga dentro de uno
 *   nocturno (22:00 → 05:59).
 * - El borrado se bloquea si hay turnos registrados en esa franja horaria (el
 *   panel anterior borraba sin comprobar y los informes ya no podían clasificar
 *   esas ventas).
 */

import { ArrowLeft, Clock, Pencil, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber } from '@/lib/utils';

import { useMutarTurnos, useTurnosCatalogo } from '../catalogos.api';
import type { Turno, TurnoPayload } from '../catalogos.types';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Hora del input (`HH:MM`) a partir del valor guardado (`HH:MM:SS`). */
function horaCorta(valor: string): string {
  return valor.slice(0, 5);
}

/** Alta/edición de un turno. */
function TurnoDialogo({
  turno,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  turno: Turno | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: TurnoPayload) => void;
  onCerrar: () => void;
}) {
  const [titulo, setTitulo] = useState(turno?.titulo ?? '');
  const [inicio, setInicio] = useState(horaCorta(turno?.inicio ?? '06:00'));
  const [fin, setFin] = useState(horaCorta(turno?.fin ?? '14:00'));

  const valido = titulo.trim().length > 0 && inicio !== '' && fin !== '' && inicio !== fin;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={turno ? `Editar turno ${turno.titulo}` : 'Nuevo turno'}
      description="Las franjas horarias no pueden solaparse. Si la hora de fin es menor que la de inicio, el turno cruza la medianoche."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() => onGuardar({ titulo: titulo.trim(), inicio, fin })}
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input
          label="Nombre del turno"
          value={titulo}
          maxLength={16}
          autoFocus
          placeholder="Ej. Diego"
          onChange={(evento) => setTitulo(evento.target.value)}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Hora de inicio"
            type="time"
            value={inicio}
            onChange={(evento) => setInicio(evento.target.value)}
          />
          <Input
            label="Hora de fin"
            type="time"
            value={fin}
            onChange={(evento) => setFin(evento.target.value)}
          />
        </div>

        {turno ? (
          <p className="text-xs text-ink-500">
            {formatNumber(turno.usos)} turno(s) registrado(s) caen en esta franja horaria.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

export function TurnosCatalogoPage() {
  useDocumentTitle('Configuración · Turnos');

  const turnos = useTurnosCatalogo();
  const mutaciones = useMutarTurnos();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; turno: Turno | null }>({
    abierto: false,
    turno: null,
  });
  const [porEliminar, setPorEliminar] = useState<Turno | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);

  const sinPermiso = turnos.error instanceof ApiError && turnos.error.status === 403;
  const lista = turnos.data?.turnos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Clock className="size-6 text-brand-500" aria-hidden="true" />
          Turnos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Franjas horarias con las que el sistema clasifica las ventas y arma los informes por
          turno.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, turno: null });
        }}
      >
        Nuevo turno
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Configuración</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: TurnoPayload) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, turno: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'No se pudo guardar el turno.')),
    };

    if (dialogo.turno) {
      mutaciones.actualizar.mutate({ id: dialogo.turno.id, payload }, alGuardar);
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const eliminar = () => {
    if (!porEliminar) return;

    mutaciones.eliminar.mutate(porEliminar.id, {
      onSuccess: (datos) => {
        setPorEliminar(null);
        setAviso({ tono: 'success', texto: datos.mensaje });
      },
      onError: (error) => {
        setPorEliminar(null);
        setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo eliminar el turno.') });
      },
    });
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {turnos.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : turnos.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer los turnos">
          {mensajeDe(turnos.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Lista de turnos</CardTitle>
            <Badge variant="neutral">{turnos.data?.total ?? 0} registrados</Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="pb-2">Turno</th>
                    <th className="w-28 pb-2">Inicio</th>
                    <th className="w-28 pb-2">Fin</th>
                    <th className="w-40 pb-2 text-center">Turnos registrados</th>
                    <th className="w-28 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((turno) => (
                    <tr key={turno.id} className="border-t border-ink-100">
                      <td className="py-3 font-medium text-ink-900">{turno.titulo}</td>
                      <td className="py-3 font-mono text-xs text-ink-700">{horaCorta(turno.inicio)}</td>
                      <td className="py-3 font-mono text-xs text-ink-700">
                        {horaCorta(turno.fin)}
                        {turno.fin < turno.inicio ? (
                          <span className="ml-2 text-[10px] uppercase text-ink-400">cruza medianoche</span>
                        ) : null}
                      </td>
                      <td className="py-3 text-center">
                        <Badge variant={turno.usos > 0 ? 'neutral' : 'outline'}>
                          {formatNumber(turno.usos)}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${turno.titulo}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, turno });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Eliminar ${turno.titulo}`}
                            title={
                              turno.usos > 0
                                ? 'No se puede eliminar: tiene turnos registrados'
                                : 'Eliminar'
                            }
                            disabled={turno.usos > 0}
                            onClick={() => setPorEliminar(turno)}
                          >
                            <Trash2 className="size-4 text-rose-500" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert variant="info" className="mt-4">
              Estas franjas se usan para <strong>clasificar las transacciones por su hora</strong>:
              por eso un turno con movimientos registrados no se puede eliminar.
            </Alert>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <TurnoDialogo
          key={`turno-${dialogo.turno?.id ?? 'nuevo'}`}
          turno={dialogo.turno}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, turno: null })}
        />
      ) : null}

      <ConfirmDialog
        open={porEliminar !== null}
        title="¿Eliminar el turno?"
        description={`Se eliminará «${porEliminar?.titulo ?? ''}» (${horaCorta(porEliminar?.inicio ?? '')} → ${horaCorta(porEliminar?.fin ?? '')}).`}
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onClose={() => setPorEliminar(null)}
        onConfirm={eliminar}
      />
    </div>
  );
}
